import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams, Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Send, CheckCircle2, Clock, MessageSquare, Check, ArrowRight, Sparkles } from 'lucide-react';
import { contactInfo } from '../data';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

const ContactPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [status, setStatus] = useState('idle');
  const [selectedSubject, setSelectedSubject] = useState('Tilbud på ophold');
  
  const formRef = useRef(null);
  const nameInputRef = useRef(null);

  // Fetch packages from the CMS
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/packages');
        const data = await response.json();
        
        // Sorter pakkerne så Golfpakke A (f.eks. Golfpakke 1 A) altid står først
        const sortedData = [...data].sort((a, b) => {
          const titleA = (a.title?.da || '').toLowerCase();
          const titleB = (b.title?.da || '').toLowerCase();
          const isA = titleA.includes('1 a') || titleA.includes('1a') || titleA.endsWith(' a');
          const isB = titleB.includes('1 a') || titleB.includes('1a') || titleB.endsWith(' a');
          if (isA && !isB) return -1;
          if (!isA && isB) return 1;
          return titleA.localeCompare(titleB);
        });

        setPackages(sortedData);

        // Check if there is a package slug in the URL query parameters
        const packageSlug = searchParams.get('package');
        if (packageSlug && sortedData.length > 0) {
          const matchedPkg = sortedData.find(
            (p) => p.package_slug?.da === packageSlug || p.slug?.da === packageSlug
          );
          if (matchedPkg) {
            setSelectedSubject(`Forespørgsel: ${matchedPkg.title.da}`);
          }
        }
      } catch (error) {
        console.error('Error fetching packages:', error);
      } finally {
        setLoadingPackages(false);
      }
    };

    fetchPackages();
    window.scrollTo(0, 0);
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setStatus('error');
    }
  };

  const handleSelectPackage = (pkgTitle) => {
    setSelectedSubject(`Forespørgsel: ${pkgTitle}`);
    
    // Scroll to form
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    
    // Focus name input after a short delay to allow scrolling
    setTimeout(() => {
      if (nameInputRef.current) {
        nameInputRef.current.focus();
      }
    }, 500);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar solid={true} />
      
      <main className="pt-20 md:pt-32 pb-0">
        {/* Hero Section */}
        <section className="pt-16 pb-20 md:pt-24 md:pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl"
            >
              <h1 className="text-5xl md:text-6xl font-semibold text-slate-900 mb-6 leading-tight">
                Lad os planlægge din <span className="text-slate-900">næste golfrejse</span> sammen
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed">
                Har du spørgsmål til vores ophold i Cádiz, ønsker du et uforpligtende tilbud, eller vil du booke en af vores populære golfpakker? Vi sidder klar til at hjælpe dig.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={formRef}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            
            {/* Contact Information */}
            <div className="lg:col-span-5 space-y-12">
              <div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-8">Kontaktinformation</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-5">
                    <div className="text-slate-900 shrink-0 pt-1">
                      <Phone size={24} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-1">Ring til os</p>
                      <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="text-xl font-semibold text-slate-900 hover:text-[#39d14b] transition-colors">
                        {contactInfo.phone}
                      </a>
                      <p className="text-slate-500 mt-1">Mandag - Fredag: 09:00 - 17:00</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-5">
                    <div className="text-slate-900 shrink-0 pt-1">
                      <Mail size={24} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-1">Send en email</p>
                      <a href={`mailto:${contactInfo.email}`} className="text-xl font-semibold text-slate-900 hover:text-[#39d14b] transition-colors">
                        {contactInfo.email}
                      </a>
                      <p className="text-slate-500 mt-1">Vi svarer altid indenfor 24 timer</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-5">
                    <div className="text-slate-900 shrink-0 pt-1">
                      <MapPin size={24} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-1">Vores base</p>
                      <p className="text-xl font-semibold text-slate-900">
                        {contactInfo.address}
                      </p>
                      <p className="text-slate-500 mt-1">Vi er selv til stede i Spanien</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-8 bg-[var(--brand)] rounded-[2.5rem] text-white relative overflow-hidden shadow-xl shadow-stone-900/10">
                <div className="relative z-10">
                  <h3 className="text-2xl font-semibold mb-6">Hvorfor vælge Golf4Life?</h3>
                  <div className="space-y-6">
                    <div className="flex gap-4">
                      <div className="mt-1 shrink-0">
                        <Check size={20} className="text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-lg">Dansk tryghed i Spanien</p>
                        <p className="text-blue-100/80">Vi kender områderne ud og ind og hjælper dig med gode råd og personlig service – både før og under dit ophold.</p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="mt-1 shrink-0">
                        <Check size={20} className="text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-lg">Socialt fællesskab</p>
                        <p className="text-blue-100/80">Vi arrangerer turneringer og middage, hvor det er muligt, så du får mulighed for at møde andre golfglade danskere og blive en del af fællesskabet.</p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="mt-1 shrink-0">
                        <Check size={20} className="text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-lg">Personlig kontakt</p>
                        <p className="text-blue-100/80">Hos Golf4Life er du ikke bare et bookingnummer. Du har direkte kontakt til os, og hvis vi ikke selv er til stede, sørger vi for, at du ved, hvem du kan kontakte.</p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Decorative ball watermark */}
                <div className="absolute -bottom-10 -right-10 opacity-10 rotate-12">
                  <img src="https://assets.auxodev.dk/assets/f3440563-2549-4ae9-b8e8-fd20a3984669/535557a3-2c33-4919-afae-181da126f5fc.png" alt="" className="w-48 h-48 object-contain" />
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-7">
              <div className="bg-white p-8 md:p-12 rounded-[3rem] shadow-2xl shadow-slate-200/50 border border-slate-100 relative">
                <div className="mb-10">
                  <h2 className="text-3xl font-semibold text-slate-900 mb-4">Send os en besked</h2>
                  <p className="text-slate-500">Udfyld formularen herunder, så kontakter vi dig hurtigst muligt.</p>
                </div>

                {status === 'success' ? (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <div className="w-24 h-24 bg-[#39d14b]/10 text-[#39d14b] rounded-full flex items-center justify-center mx-auto mb-8">
                      <CheckCircle2 size={48} />
                    </div>
                    <h3 className="text-3xl font-semibold text-slate-900 mb-4 uppercase">Beskeden er sendt!</h3>
                    <p className="text-slate-600 text-lg mb-10 max-w-md mx-auto">
                      Tak for din henvendelse. Vi har modtaget dine informationer og vender tilbage til dig indenfor kort tid.
                    </p>
                    <button 
                      onClick={() => setStatus('idle')}
                      className="px-10 py-4 bg-[#39d14b] text-white font-semibold rounded-2xl hover:-translate-y-1 transition-all shadow-lg shadow-[#39d14b]/20 uppercase tracking-wider"
                    >
                      Send en ny besked
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Honeypot field */}
                    <div className="sr-only absolute opacity-0 pointer-events-none" aria-hidden="true">
                      <label htmlFor="website_url">Website URL</label>
                      <input type="text" name="website_url" id="website_url" tabIndex="-1" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-3">
                        <label htmlFor="name" className="text-sm font-semibold text-slate-900 uppercase tracking-wider ml-1">Dit Navn</label>
                        <input 
                          type="text" 
                          id="name" 
                          name="name" 
                          ref={nameInputRef}
                          required 
                          placeholder="F.eks. Erik Jensen"
                          className="w-full px-6 py-5 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-[#39d14b] focus:border-[#39d14b] outline-none transition-all text-slate-900 placeholder:text-slate-400"
                        />
                      </div>
                      <div className="space-y-3">
                        <label htmlFor="email" className="text-sm font-semibold text-slate-900 uppercase tracking-wider ml-1">Din Email</label>
                        <input 
                          type="email" 
                          id="email" 
                          name="email" 
                          required 
                          placeholder="erik@mail.dk"
                          className="w-full px-6 py-5 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-[#39d14b] focus:border-[#39d14b] outline-none transition-all text-slate-900 placeholder:text-slate-400"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-3">
                        <label htmlFor="phone" className="text-sm font-semibold text-slate-900 uppercase tracking-wider ml-1">Telefonnummer</label>
                        <input 
                          type="tel" 
                          id="phone" 
                          name="phone" 
                          placeholder="12 34 56 78"
                          className="w-full px-6 py-5 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-[#39d14b] focus:border-[#39d14b] outline-none transition-all text-slate-900 placeholder:text-slate-400"
                        />
                      </div>
                      <div className="space-y-3">
                        <label htmlFor="subject" className="text-sm font-semibold text-slate-900 uppercase tracking-wider ml-1">Hvad drejer det sig om?</label>
                        <div className="relative">
                          <select 
                            id="subject" 
                            name="subject"
                            value={selectedSubject}
                            onChange={(e) => setSelectedSubject(e.target.value)}
                            className="w-full px-6 py-5 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-[#39d14b] focus:border-[#39d14b] outline-none transition-all text-slate-900 appearance-none pr-12"
                          >
                            <option value="Tilbud på ophold">Tilbud på ophold</option>
                            {packages.map((pkg) => (
                              <option key={pkg.id} value={`Forespørgsel: ${pkg.title.da}`}>
                                Forespørgsel: {pkg.title.da}
                              </option>
                            ))}
                            <option value="Spørgsmål til destination">Spørgsmål til destination</option>
                            <option value="Andet">Andet</option>
                          </select>
                          <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <label htmlFor="message" className="text-sm font-semibold text-slate-900 uppercase tracking-wider ml-1">Din besked</label>
                      <textarea 
                        id="message" 
                        name="message" 
                        rows="6" 
                        required 
                        placeholder="Skriv lidt om dine ønsker til rejsen..."
                        className="w-full px-6 py-5 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-[#39d14b] focus:border-[#39d14b] outline-none transition-all text-slate-900 placeholder:text-slate-400 resize-none"
                      ></textarea>
                    </div>

                    <button 
                      type="submit" 
                      disabled={status === 'loading'}
                      className="w-full py-6 bg-[#39d14b] text-white font-semibold text-xl rounded-2xl hover:-translate-y-1 transition-all shadow-xl shadow-[#39d14b]/20 flex items-center justify-center gap-4 disabled:opacity-70 disabled:cursor-not-allowed group"
                    >
                      {status === 'loading' ? (
                        <div className="w-7 h-7 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                      ) : (
                        <>
                          <span className="uppercase tracking-wider">Send besked nu</span>
                          <Send size={22} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                        </>
                      )}
                    </button>
                    
                    {status === 'error' && (
                      <p className="text-red-500 text-sm text-center font-bold">
                        Der skete en fejl. Prøv venligst igen eller ring til os direkte.
                      </p>
                    )}
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Packages Section */}
        <section className="mt-32 py-24 bg-slate-50 border-t border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#39d14b]/10 text-[#39d14b] rounded-full text-sm font-semibold uppercase tracking-wider mb-4">
                <Sparkles size={16} />
                <span>Vores Golfpakker</span>
              </div>
              <h2 className="text-4xl font-semibold text-slate-900 mb-4 uppercase">
                Vælg den perfekte golfpakke
              </h2>
              <p className="text-lg text-slate-600">
                Vi tilbyder skræddersyede golfrejser med alt inkluderet. Vælg en af vores populære pakker herunder for at starte din forespørgsel.
              </p>
            </div>

            {loadingPackages ? (
              <div className="flex justify-center py-12">
                <div className="w-12 h-12 border-4 border-[#39d14b] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {packages.map((pkg, index) => (
                  <motion.div
                    key={pkg.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex flex-col bg-white rounded-3xl overflow-hidden border border-slate-200/60 shadow-sm hover:shadow-xl transition-all duration-300 group h-full"
                  >
                    <div className="aspect-[16/10] overflow-hidden relative">
                      <img 
                        src={pkg.image[0]} 
                        alt={pkg.title.da} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-1.5 rounded-full text-sm font-bold text-slate-900 shadow-sm">
                        {pkg.price_text.da}
                      </div>
                    </div>
                    
                    <div className="p-8 flex flex-col flex-grow">
                      <h3 className="text-2xl font-semibold text-slate-900 mb-3 uppercase leading-tight">
                        {pkg.title.da}
                      </h3>
                      <p className="text-slate-600 text-sm mb-6 leading-relaxed">
                        {pkg.description.da}
                      </p>
                      
                      <div className="text-slate-600 text-sm leading-relaxed mb-8 flex-grow border-t border-slate-100 pt-6">
                        <p className="font-semibold text-slate-900 mb-3 uppercase tracking-wider text-xs">Hvad er inkluderet:</p>
                        <div 
                          dangerouslySetInnerHTML={{ __html: pkg.details.da }} 
                          className="prose prose-slate prose-sm max-w-none [&>ul]:list-disc [&>ul]:pl-4 [&>li]:mb-2 [&>li]:text-slate-600" 
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4 mt-auto">
                        <button
                          onClick={() => handleSelectPackage(pkg.title.da)}
                          className="py-3.5 px-4 bg-[#39d14b] text-white text-center text-xs font-bold rounded-xl hover:bg-[#2eb83e] transition-colors uppercase tracking-wider shadow-md shadow-[#39d14b]/10"
                        >
                          Vælg pakke
                        </button>
                        <Link 
                          to={`/pakker/${pkg.package_slug?.da || pkg.slug?.da}`}
                          className="py-3.5 px-4 bg-slate-100 text-slate-800 text-center text-xs font-bold rounded-xl hover:bg-slate-200 transition-colors uppercase tracking-wider flex items-center justify-center gap-1 group"
                        >
                          <span>Læs mere</span>
                          <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Tillæg & Tilvalg */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="mt-16 bg-white rounded-3xl p-8 md:p-10 border border-slate-200/80 shadow-sm"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-slate-100 mb-8">
                <div>
                  <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-[#39d14b]/10 text-[#39d14b] rounded-full text-xs font-bold uppercase tracking-wider mb-2">
                    <Sparkles size={14} />
                    <span>Ekstra muligheder</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-semibold text-slate-900 uppercase">
                    Tillæg & Tilvalg
                  </h3>
                </div>
                <p className="text-sm text-slate-500 max-w-md">
                  Gør dit ophold endnu mere fleksibelt med vores tilkøbsmuligheder for udstyr, ekstra greenfees og overnattende gæster.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Trolley Leje */}
                <div className="p-6 rounded-2xl bg-slate-50 border border-slate-100 flex flex-col justify-between">
                  <div>
                    <h4 className="font-semibold text-slate-900 text-lg mb-4 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#39d14b]"></span>
                      Leje af Trolley
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-baseline justify-between py-2 border-b border-slate-200/60">
                        <span className="text-slate-700 text-sm font-medium">Leje af Trolley 2 hjul i en måned</span>
                        <span className="text-slate-900 font-bold text-base whitespace-nowrap ml-2">45 €</span>
                      </div>
                      <div className="flex items-baseline justify-between py-2">
                        <span className="text-slate-700 text-sm font-medium">Leje af Trolley 3 hjul i en måned</span>
                        <span className="text-slate-900 font-bold text-base whitespace-nowrap ml-2">55 €</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-4 pt-3 border-t border-slate-200/50">
                    Gælder for en hel måneds leje.
                  </p>
                </div>

                {/* Greenfees */}
                <div className="p-6 rounded-2xl bg-slate-50 border border-slate-100 flex flex-col justify-between">
                  <div>
                    <h4 className="font-semibold text-slate-900 text-lg mb-4 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#39d14b]"></span>
                      Ekstra Greenfees (GF)
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-baseline justify-between py-2 border-b border-slate-200/60">
                        <span className="text-slate-700 text-sm font-medium">Løse GF til La Estancia / S.P Hills</span>
                        <span className="text-slate-900 font-bold text-base whitespace-nowrap ml-2">45 ,-€ /stk.</span>
                      </div>
                      <div className="flex items-baseline justify-between py-2">
                        <span className="text-slate-700 text-sm font-medium">Campano</span>
                        <span className="text-slate-900 font-bold text-base whitespace-nowrap ml-2">55,-€ /GF</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-600 mt-4 pt-3 border-t border-slate-200/50 leading-relaxed">
                    Mulighed for køb af ekstra GF på de baner Golf4life har aftaler med, til meget gunstige priser.
                  </p>
                </div>

                {/* Gæster & Ekstra uger */}
                <div className="p-6 rounded-2xl bg-slate-50 border border-slate-100 flex flex-col justify-between">
                  <div>
                    <h4 className="font-semibold text-slate-900 text-lg mb-4 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#39d14b]"></span>
                      Gæster & Ekstra uger
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-baseline justify-between py-2 border-b border-slate-200/60">
                        <span className="text-slate-700 text-sm font-medium">Pris for gæster der overnatter</span>
                        <span className="text-slate-900 font-bold text-sm whitespace-nowrap ml-2 text-right">10 € / voksne gæst / døgn</span>
                      </div>
                      <div className="py-2 flex items-baseline justify-between">
                        <span className="text-slate-700 text-sm font-medium">Ekstra uger</span>
                        <span className="text-slate-900 font-semibold text-xs whitespace-nowrap ml-2">Spørg om pris</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-200/50 flex items-center justify-between">
                    <span className="text-xs text-slate-500">Spørg om pris for ekstra uger.</span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedSubject('Forespørgsel: Tillæg & Tilvalg');
                        if (formRef.current) {
                          formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }}
                      className="text-xs font-bold text-[#39d14b] hover:text-[#2eb83e] flex items-center gap-1 uppercase tracking-wider transition-colors"
                    >
                      <span>Spørg her</span>
                      <ArrowRight size={12} />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQ Preview / Trust Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-semibold text-slate-900 mb-16 uppercase tracking-widest">Ofte stillede spørgsmål</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-left">
              <div className="space-y-4">
                <div className="text-slate-900">
                  <Clock size={24} />
                </div>
                <h4 className="text-xl font-semibold text-slate-900">Hvornår får jeg svar?</h4>
                <p className="text-slate-600">Vi bestræber os på at svare på alle henvendelser indenfor 24 timer – ofte meget hurtigere.</p>
              </div>
              <div className="space-y-4">
                <div className="text-slate-900">
                  <MessageSquare size={24} />
                </div>
                <h4 className="text-xl font-semibold text-slate-900">Er tilbuddet bindende?</h4>
                <p className="text-slate-600">Nej, alle tilbud og forslag vi sender er 100% uforpligtende, indtil du vælger at booke.</p>
              </div>
              <div className="space-y-4">
                <div className="text-slate-900">
                  <CheckCircle2 size={24} />
                </div>
                <h4 className="text-xl font-semibold text-slate-900">Hjælper I med fly?</h4>
                <p className="text-slate-600">Vi rådgiver gerne om de bedste flyforbindelser til Málaga, så du får en nem rejse.</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
